import { useEffect } from "react";
import { api } from "@/lib/api";

/**
 * Batch 21 — GA4 + Microsoft Clarity script injector.
 *
 * Loads once on mount. Fetches admin-configured IDs from
 * `GET /api/public/analytics-config`. If either ID is unset or disabled the
 * corresponding script is NOT injected (fails closed, no console errors).
 * All work happens inside `useEffect` so SSR/first-paint is untouched.
 */
function addScriptOnce(id, src) {
    if (typeof document === "undefined") return;
    if (document.getElementById(id)) return;
    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.src = src;
    s.onerror = () => { /* stay silent — never disrupt UX */ };
    document.head.appendChild(s);
}

export default function AnalyticsLoader() {
    useEffect(() => {
        let cancelled = false;
        (async () => {
            let cfg = null;
            try {
                const { data } = await api.get("/public/analytics-config");
                cfg = data;
            } catch { return; }
            if (cancelled || !cfg) return;

            // ---- GA4 (gtag.js) ----
            const ga4 = cfg.ga4_enabled && cfg.ga4_measurement_id ? cfg.ga4_measurement_id : "";
            if (ga4 && /^G-[A-Z0-9]+$/i.test(ga4)) {
                try {
                    window.dataLayer = window.dataLayer || [];
                    window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
                    window.gtag("js", new Date());
                    window.gtag("config", ga4, {
                        send_page_view: true,
                        anonymize_ip: true,
                    });
                    addScriptOnce("ga4-gtag", `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ga4)}`);
                } catch { /* swallow */ }
            }

            // ---- Microsoft Clarity ----
            const clarity = cfg.clarity_enabled && cfg.clarity_project_id ? cfg.clarity_project_id : "";
            if (clarity && /^[A-Za-z0-9_-]+$/.test(clarity) && clarity.length <= 64) {
                try {
                    if (!document.getElementById("clarity-boot")) {
                        const boot = document.createElement("script");
                        boot.id = "clarity-boot";
                        boot.text =
                            "(function(c,l,a,r,i,t,y){c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};" +
                            "t=l.createElement(r);t.async=1;t.src='https://www.clarity.ms/tag/'+i;" +
                            "y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);" +
                            "})(window,document,'clarity','script','" + clarity.replace(/'/g, "") + "');";
                        document.head.appendChild(boot);
                    }
                } catch { /* swallow */ }
            }
        })();
        return () => { cancelled = true; };
    }, []);
    return null;
}
